import { useState, useEffect, useRef, useCallback } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { navigationConfig as staticNav, siteMeta as staticMeta } from '../../data/navigation'

const API = import.meta.env.VITE_API_URL || '/api'

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const [indicator, setIndicator] = useState({ left: 0, width: 0 })
  const [nav, setNav] = useState(staticNav)
  const [meta, setMeta] = useState(staticMeta)
  const location = useLocation()
  const navRef = useRef<HTMLDivElement>(null)
  const linkRefs = useRef<(HTMLAnchorElement | null)[]>([])

  useEffect(() => {
    fetch(`${API}/navigation`).then(r => r.json()).then(d => {
      if (d && d.links) {
        setNav({ links: d.links.filter((l: any) => l.label && l.path), cta: { label: d.cta_label || 'Get a Quote', path: d.cta_path || '/contact' } })
        setMeta({ name: d.site_name || staticMeta.name, tagline: d.site_tagline || staticMeta.tagline, description: d.site_description || staticMeta.description, url: d.site_url || staticMeta.url, logo: d.site_logo || staticMeta.logo, favicon: d.site_favicon || staticMeta.favicon, headingFont: d.heading_font || staticMeta.headingFont, bodyFont: d.body_font || staticMeta.bodyFont })
      }
    }).catch(() => {})
  }, [])

  const updateIndicator = useCallback(() => {
    const idx = nav.links.findIndex(l => location.pathname === l.path)
    if (idx >= 0 && linkRefs.current[idx]) {
      const el = linkRefs.current[idx]!
      setIndicator({ left: el.offsetLeft, width: el.offsetWidth })
    } else {
      setIndicator({ left: 0, width: 0 })
    }
  }, [location.pathname])

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20)
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  useEffect(() => {
    setIsOpen(false)
  }, [location.pathname])

  useEffect(() => {
    updateIndicator()
  }, [updateIndicator])

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? 'bg-gray-950/80 backdrop-blur-xl border-b border-white/5 shadow-2xl shadow-black/20'
          : 'bg-transparent'
      }`}
      role="navigation"
      aria-label="Main navigation"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link
            to="/"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="flex items-center gap-3 group"
            aria-label={`${meta.name} — Home`}
          >
            <div className="relative w-14 h-14 flex-shrink-0">
              <img
                src={meta.logo}
                alt={`${meta.name} logo`}
                className="w-14 h-14 object-contain"
                onError={(e) => {
                  ;(e.target as HTMLImageElement).style.display = 'none'
                  const parent = (e.target as HTMLImageElement).parentElement
                  if (parent) {
                    parent.innerHTML = `<div class="w-14 h-14 rounded-xl bg-gradient-to-br from-electric-blue to-electric-purple flex items-center justify-center text-white font-bold text-2xl">SST</div>`
                  }
                }}
              />
            </div>
            <span className="text-xl font-bold text-white hidden sm:block">
              {meta.name}
            </span>
          </Link>

          {/* Desktop Nav */}
          <div
            ref={navRef}
            className="hidden lg:flex items-center gap-1 relative"
          >
            {/* Spotlight glow behind active link */}
            <motion.div
              className="absolute -top-1 left-0 h-10 w-20 pointer-events-none rounded-full bg-gradient-to-r from-electric-blue/20 to-electric-purple/20 blur-2xl"
              animate={{
                left: indicator.left - 8,
                width: indicator.width + 16,
                opacity: indicator.width > 0 ? 1 : 0,
              }}
              transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            />
            {/* Sliding gradient underline */}
            <motion.div
              className="absolute -bottom-0.5 left-0 h-[2px] pointer-events-none rounded-full bg-gradient-to-r from-electric-blue to-electric-purple"
              animate={{
                left: indicator.left,
                width: indicator.width,
                opacity: indicator.width > 0 ? 1 : 0,
              }}
              transition={{ type: 'spring', stiffness: 400, damping: 35 }}
            />
            {nav.links.map((link, i) => {
              const isActive = location.pathname === link.path
              return (
                <Link
                  key={link.path}
                  ref={(el) => { linkRefs.current[i] = el }}
                  to={link.path}
                  className={`relative px-4 py-2 text-sm font-medium rounded-lg transition-all duration-300 z-10 ${
                    isActive
                      ? 'text-white'
                      : 'text-gray-400 hover:text-white'
                  }`}
                >
                  {link.label}
                </Link>
              )
            })}
            <Link
              to={nav.cta.path}
              className="ml-4 px-6 py-2.5 text-sm font-semibold text-white rounded-full bg-gradient-to-r from-electric-blue to-electric-purple hover:shadow-lg hover:shadow-electric-blue/25 transition-all duration-300 hover:scale-105"
            >
              {nav.cta.label}
            </Link>
          </div>

          {/* Mobile toggle */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="lg:hidden relative w-10 h-10 flex items-center justify-center text-white"
            aria-label={isOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={isOpen}
          >
            <div className="w-6 flex flex-col gap-1.5">
              <motion.span
                animate={isOpen ? { rotate: 45, y: 7 } : { rotate: 0, y: 0 }}
                className="block h-0.5 w-full bg-white rounded-full"
              />
              <motion.span
                animate={isOpen ? { opacity: 0 } : { opacity: 1 }}
                className="block h-0.5 w-full bg-white rounded-full"
              />
              <motion.span
                animate={isOpen ? { rotate: -45, y: -7 } : { rotate: 0, y: 0 }}
                className="block h-0.5 w-full bg-white rounded-full"
              />
            </div>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden border-t border-white/5 bg-gray-950/95 backdrop-blur-xl"
          >
            <div className="max-w-7xl mx-auto px-4 py-6 flex flex-col gap-2">
              {nav.links.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`px-4 py-3 text-base font-medium rounded-xl transition-all ${
                    location.pathname === link.path
                      ? 'text-white bg-white/10'
                      : 'text-gray-400 hover:text-white hover:bg-white/5'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <Link
                to={nav.cta.path}
                className="mt-4 px-6 py-3.5 text-center text-base font-semibold text-white rounded-xl bg-gradient-to-r from-electric-blue to-electric-purple"
              >
                {nav.cta.label}
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  )
}
